// src/pages/Subscriptions.jsx
import { useCallback, useEffect, useRef, useState } from 'react';
import './Subscriptions.scss';
import './Landing.scss';
import './Home.scss';
import upload from '/icons/upload.svg';
import Heading from '../components/ui/Heading';
import package_step1 from '/icons/package-step1.svg';
import package_step2 from '/icons/package-step2.svg';
import package_step3 from '/icons/package-step3.svg';
import package_step4 from '/icons/package-step4.svg';
import facebook from '/icons/facebook.svg';
import dog from '/images/dog_upload-field.png';
import packages from '/images/packages.png';
import Button from '../components/ui/Button';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import "swiper/css/pagination";
import { useDispatch, useSelector } from 'react-redux';
import useFileHandlers from '../hooks/useFileHandlers';
import { resetCustomizer, selectUploadedImageIds, setCustomizerStep } from '../store/customizerSlice';
import { getImageFromDB, resetIndexedDB, saveImageToDB } from '../utils/indexedDB';
import { resetOrderDetails } from '../store/checkoutSlice';
import { useAffiliate } from '../hooks/useAffiliate';
import { Oval } from 'react-loader-spinner';
import FaqAccordion from '../sections/FaqAccordion';
import { ArrowRight } from 'lucide-react';
import PetCustomizer from '../components/customizer/PetCustomizer';
import SubscriptionProducts from '../components/product/SubscriptionProducts';
import { scrollToId } from '../utils/scrollToId';
import { useAppNavigate } from '../hooks/useAppNavigate';
import { trackCTA } from '../utils/trackCTA';
import { useLocation } from 'react-router-dom';

const testimonials = [
  {
    id: 1,
    heading: 'Love it!',
    text: 'The quality is amazing, and the monthly delivery makes it so easy. Our cat gets excited every time the box arrives',
    name: 'Jake & Luna',
    avatar: '/images/cat-photo.jpg',
    facebook_link: 'https://www.facebook.com/PetTreatink',
  },
  {
    id: 2,
    heading: 'Lovely treats!',
    text: 'My dog absolutely loves these treats, and I love seeing his face on the bag every month! It\'s such a fun!',
    name: 'Emily & Max',
    avatar: '/images/cat-photo.jpg',
    facebook_link: 'https://www.facebook.com/PetTreatink',
  },
  {
    id: 3,
    heading: 'Love it!',
    text: 'My dog absolutely loves these treats, and I love seeing his face on the bag every month! It\'s such a fun!',
    name: 'Jake & Luna',
    avatar: '/images/cat-photo.jpg',
    facebook_link: 'https://www.facebook.com/PetTreatink',
  },
  {
    id: 4,
    heading: 'Lovely treats!',
    text: 'My dog absolutely loves these treats, and I love seeing his face on the bag every month! It\'s such a fun!',
    name: 'Emily & Max',
    avatar: '/images/cat-photo.jpg',
    facebook_link: 'https://www.facebook.com/PetTreatink',
  }
];

const faqs = [
  {
    question: 'Can I pause or cancel my subscription anytime?',
    answer: 'Yes! You can pause or cancel anytime from your account settings.',
  },
  {
    question: 'What if I have more than one pet?',
    answer: 'You can create profiles for each pet and order accordingly.',
  },
  {
    question: 'Are the treats safe for all pets?',
    answer: 'Our treats are made with vet-approved ingredients and are safe for most dogs and cats. Always check with your vet if your pet has allergies.',
  },
  {
    question: 'How long does delivery take?',
    answer: 'Delivery typically takes 3–5 business days after shipping.',
  },
  {
    question: 'Can I gift a subscription to someone else?',
    answer: 'Yes! You can gift a subscription during checkout by entering their shipping info.',
  },
];

function Subscriptions({ isMobile }) {
  const dispatch = useDispatch();
  const navigate = useAppNavigate();
  const location = useLocation();
  const { handleFileSelect, handleDrop } = useFileHandlers();
  const cartItems = useSelector((state) => state.cart.cartItems);
  const { affiliate, isLoading } = useAffiliate();
  const [currentStep, setCurrentStep] = useState(1);
  const [subscriptionQuantity, setSubscriptionQuantity] = useState(0);

  // Extract and save promo code from URL
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const promoCode = searchParams.get('promo');
    
    if (promoCode) {
      console.log('Promo code found in URL:', promoCode);
      // Store the promo code in localStorage so it persists through the checkout flow
      localStorage.setItem('promoCode', promoCode);
    }
  }, [location.search]);

  //Reset customizer states
  const resetStates = () => {
    localStorage.removeItem("petType");
    localStorage.removeItem("petName");
    localStorage.removeItem("imageMetadata");
    localStorage.removeItem("label_template");
    // Note: We don't remove promoCode here as it should persist to checkout
    resetIndexedDB("uploadedImages", cartItems);
    resetIndexedDB("resultImages", cartItems);
    resetIndexedDB("onProductImages", cartItems);
    dispatch(setCustomizerStep(1));
    dispatch(resetOrderDetails());
    dispatch(resetCustomizer());
    setCurrentStep(1);
    setSubscriptionQuantity(0);
  };

  useEffect(() => {
    resetStates();
  }, []);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center w-full h-screen">
        <Oval
          visible={true}
          height={50}
          width={50}
          color="#a99cdf"
          strokeWidth="3"
          strokeWidthSecondary="3"
          secondaryColor="#e2e6ff"
          ariaLabel="oval-loading"
        />
      </div>
    );
  }

  return (
    <div className='subscriptions-page landing-page'>
      <div className='home-hero full-width'>
        <div className='flex-container contained-width'>
          <div className={`flex flex-col gap-3 ${!affiliate ? 'md:gap-5' : ''} mt-0 md:mt-0`}>
            {affiliate && (
              <div className='affiliate-logo-cont flex w-full justify-start md:mt-0'>
                <img
                  src={affiliate.logo}
                  alt={`${affiliate.name} logo`}
                  className='affiliate-logo'
                />
              </div>
            )}
            <h1>Treats with Love<br />Every Month</h1>
            <p className='text-mid mb-8'>
              Get personalized, vet-approved treats delivered monthly with your pet featured on the label.
            </p>
            <Button
              type="button"
              variant="filled"
              style={{ maxWidth: 'fit-content' }}
              onClick={() => {
                scrollToId('start-subscription');
                trackCTA('Start Personalizing');
              }}
            >
              Start Personalizing
            </Button>

          </div>
          <div className='flex items-center justify-end'>
              {affiliate ? (
                <img 
                  src={affiliate.featured_image_url} 
                  alt={`${affiliate.name} featured image`} 
                  className="w-full max-w-none md:max-w-[500px]" 
                />
              ) : (
                <img 
                  src={packages} 
                  alt="" 
                  className="w-full max-w-none md:max-w-[500px]" 
                />
              )}
          </div>
        </div>

        <div className='instructions contained-width  bg-white rounded-10 mt-8 md:mt-5'>
          <div className='p-4 md:p-8 flex justify-center gap-8 flex-col md:flex-row'>
            <div className='flex gap-4'>
              <img src={package_step1} alt="" />
              <div className='flex flex-col gap-2'>
                <h5 className="form-title body-heading">Vet-Approved</h5>
                <p className="text-sm mb-0">Wholesome, safe ingredients — loved by pets, approved by vets.</p>
              </div>
            </div>

            <div className='flex gap-4'>
              <img src={package_step2} alt="" />
              <div className='flex flex-col gap-2'>
                <h5 className="form-title body-heading">Custom Labels</h5>
                <p className="text-sm mb-0">Add your pet's photo and make treat time extra special.</p>
              </div>
            </div>

            <div className='flex gap-4'>
              <img src={package_step3} alt="" />
              <div className='flex flex-col gap-2'>
                <h5 className="form-title body-heading">Monthly Delivery</h5>
                <p className="text-sm mb-0">Get fresh, personalized treats delivered to your door every month.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <section className="bundle-container full-width flex justify-center" id="start-subscription">
        <div className="contained-width flex flex-col gap-10 items-center">
          <Heading
            h4Text=""
            h2Text="Start Your Subscription Now!"
            h4Size="small"
            h2Size="small"
            align="center"
            h4Color="secondary"
          />
          <div className="bundle-inner rounded-20 p-12 flex flex-col gap-10 items-center">
            <h3 className={`text-xl md:text-3xl text-center`}>
              {currentStep === 1 && 'Step 1: Pick Your Perfect Bundle'}
              {(currentStep === 2 || currentStep === 3) && 'Step 2: Personalize Your Treats'}
              {currentStep === 4 && 'Step 3: Select Product'}
            </h3>
            <div className={`${currentStep === 1 ? '' : 'hidden'} flex flex-col gap-10 items-center fade-step`}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="rounded-20 px-8 box-shadow py-8 gap-4 flex flex-col items-center">
                  <div className="flex flex-col justiy-center items-center gap-4 md:gap-8">
                    <img src={package_step2} alt="" />
                    <h4 className="form-title body-heading text-center">2 Bags Per Month</h4>
                    <p className="text-center max-w-[270px]">Perfect for small pets or light treat days.</p>
                  </div>
                  <Button
                    type="button"
                    variant={subscriptionQuantity === 2 ? 'filled' : 'outlined'}
                    onClick={() => { setSubscriptionQuantity(2) }}
                    style={{ maxWidth: 'fit-content' }}
                  >
                    Choose
                  </Button>
                </div>
                <div className="rounded-20 px-8 box-shadow py-8 gap-4 flex flex-col items-center">
                  <div className="flex flex-col justiy-center items-center gap-4 md:gap-8">
                    <img src={package_step4} alt="" />
                    <h4 className="form-title body-heading text-center">4 Bags Per Month</h4>
                    <p className="text-center max-w-[270px]">Great for bigger appetites or extra spoiling.</p>
                  </div>
                  <Button
                    type="button"
                    variant={subscriptionQuantity === 4 ? 'filled' : 'outlined'}
                    style={{ maxWidth: 'fit-content' }}
                    onClick={() => { setSubscriptionQuantity(4) }}
                  >
                    Choose
                  </Button>
                </div>
              </div>
              <Button
                type="button"
                onClick={() => {
                  setCurrentStep(2);
                  scrollToId('start-subscription');
                }}
                variant="filled"
                className="gap-4"
                disabled={subscriptionQuantity === 0}
                style={{ maxWidth: 'fit-content' }}>
                Next Step<ArrowRight size={20} />
              </Button>
            </div>

            <div className={`${currentStep === 2 ? '' : 'hidden'} flex flex-col gap-10 items-center w-full fade-step`}>
              <section className='upload-container w-full'>
                <div className='contained-width flex justify-center rounded-20 overflow-hidden'>
                  <div
                    className="upload-area w-full border-3 border-dashed rounded-20 flex items-center justify-center cursor-pointer transition"
                    onClick={() => document.getElementById('fileInput').click()}
                    onDrop={(e) => {
                      handleDrop(e, false);
                      setCurrentStep(3);
                    }}
                    onDragOver={(e) => e.preventDefault()}
                  >
                    <div className="flex flex-col items-center">
                      <img src={upload} alt="" />
                      {isMobile ?
                        <p>Select your pet's photo<br />
                          and start personalizing!</p>
                        :
                        <p>Drag your pet's photo here<br />
                          and start personalizing!</p>
                      }
                    </div>
                    <input
                      id="fileInput"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        handleFileSelect(e, false);
                        setCurrentStep(3);
                        scrollToId('start-subscription');
                      }}
                    />
                  </div>
                </div>
              </section>
            </div>

            <div className={`${currentStep === 3 ? '' : 'hidden'} flex flex-col gap-10 items-center w-full`}>
              {(currentStep === 3 || currentStep === 4) && (
                <PetCustomizer
                  subscriptionPage={true}
                  setSubscriptionPageStep={setCurrentStep}
                  onSubmit={() => scrollToId('start-subscription')}
                  isMobile={isMobile}
                />
              )}
            </div>

            <div className={`${currentStep === 4 ? '' : 'hidden'} flex flex-col gap-10 items-center w-full`}>
              <div className='slider-products w-full'>
                {currentStep === 4 &&
                  <SubscriptionProducts
                    subscriptionQuantity={subscriptionQuantity}
                    setCurrentStep={setCurrentStep}
                  />}
              </div>
            </div>

            <div className={`${currentStep === 5 ? '' : 'hidden'} flex flex-col gap-10 items-center w-full`}>
              <div className='flex flex-col md:flex-row gap-4 w-full justify-center items-center'>
                <Button
                  type="button"
                  onClick={() => {
                    resetStates();
                    scrollToId('start-subscription');
                  }}
                  variant="outlined"
                  className="gap-4"
                  style={{ maxWidth: 'fit-content' }}>
                  Start a New Subscription
                </Button>
                <Button
                  type="button"
                  onClick={() => {
                    setCurrentStep(1);
                    navigate('/checkout');
                  }}
                  variant="filled"
                  className="gap-4"
                  style={{ maxWidth: 'fit-content' }}>
                  Go to Cart<ArrowRight size={20} />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="why-us-container full-width flex justify-center pt-10 md:pt-5" id="how-it-works">
        <div className="contained-width flex flex-col gap-10 items-center">
          <Heading
            h4Text="It's easy!"
            h2Text="How It Works"
            h4Size="small"
            h2Size="small"
            align="center"
            h4Color="secondary"
          />
          <div className="card-boxes grid grid-cols-3 gap-5">
            <div className="card-box1 rounded-20 box-shadow-sm py-8 gap-8 flex flex-col items-center justify-end px-3 md:px-8 max-w-[300px] md:max-w-none">
              <div className="flex flex-col justiy-center items-center gap-1 md:gap-3">
                <h5 className="form-title body-heading text-center">Pick Your Bundle</h5>
                <p className="text-center mb-0">Choose a 2 or 4 bag monthly subscription — perfect for light snackers or treat lovers.</p>
              </div>
            </div>
            <div className="card-box2 rounded-20 box-shadow-sm py-8 gap-8 flex flex-col items-center justify-end px-3 md:px-8 max-w-[300px] md:max-w-none">
              <div className="flex flex-col justiy-center items-center gap-1 md:gap-3">
                <h5 className="form-title body-heading text-center">Customize Treats</h5>
                <p className="text-center mb-0">Upload your pet's photo, pick a flavor, and design a label that's all about them.</p>
              </div>
            </div>
            <div className="card-box3 rounded-20 box-shadow-sm py-8 gap-8 flex flex-col items-center justify-end px-3 md:px-8 max-w-[300px] md:max-w-none">
              <div className="flex flex-col justiy-center items-center gap-1 md:gap-3">
                <h5 className="form-title body-heading text-center">Get Deliveries</h5>
                <p className="text-center mb-0">We'll send fresh, personalized treats to your door every month — easy, fun, and full of love.</p>
              </div>
            </div>
          </div>
          <div className='flex justify-center'>
            <Button
              type="button"
              variant="filled"
              style={{ maxWidth: 'fit-content' }}
              onClick={() => {
                scrollToId('start-subscription');
                trackCTA('Get Started');
              }}
            >
              Get Started
            </Button>
          </div>
        </div>
      </section>

      <section className="testimonials-container full-width flex justify-center" id="testimonials">
        <div className="contained-width flex flex-col gap-5 md:gap-10">
          <Heading
            h4Text="Testimomials"
            h2Text="What Pet Parents Are Saying"
            h4Size="small"
            h2Size="small"
            align="center"
            h4Color="secondary"
          />
          <div className='testimonials-slider relative'>
            <Swiper
              modules={[Pagination, Navigation]}
              pagination={{ el: ".slider-pagination", clickable: true }}
              navigation={{
                nextEl: '.swiper-button-next',
                prevEl: '.swiper-button-prev',
              }}
              loop={true}
              spaceBetween={10}
              slidesOffsetAfter={0}
              slidesPerGroup={1}
              breakpoints={{
                300: { slidesPerView: 1 },
                700: { slidesPerView: 2 },
                1000: { slidesPerView: 2 },
              }}
            >
              {testimonials.map((testimonial, index) => (
                <SwiperSlide key={index} className='px-5 md:px-10 py-6 rounded-20'>
                  <h5 className='body-heading'>{testimonial.heading}</h5>
                  <p className='text-sm'><i>{testimonial.text}</i></p>
                  <div className='flex gap-4 items-center justify-between'>
                    <div className='flex gap-4 items-center'>
                      {testimonial.avatar &&
                        <img className="avatar" src={testimonial.avatar} alt="" />
                      }
                      <p className='mb-0'>{testimonial.name}</p>
                    </div>
                    {testimonial.facebook_link &&
                      <a href={testimonial.facebook_link} target="_blank" className="flex items-center gap-2">
                        <img src={facebook} alt="" />
                      </a>
                    }
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
            <div className="swiper-button-prev absolute left-[-40px] top-1/2 transform -translate-y-1/2 z-10 cursor-pointer" />
            <div className="swiper-button-next absolute right-[-40px] top-1/2 transform -translate-y-1/2 z-10 cursor-pointer" />
            <div className="slider-pagination mt-10" />
          </div>
        </div>
      </section>

      <section className="faq-container full-width flex justify-center pb-10 pt-10" id="faq">
        <div className="contained-width flex gap-0 md:gap-10 flex-col-reverse md:flex-row">
          <div className='image-block flex justify-center basis-[100%] md:basis-[30%] p-8'>
            <img src={dog} alt="" />
          </div>
          <div className='flex flex-col gap-5 md:gap-10 basis-[100%] md:basis-[70%]'>
            <Heading
              h4Text="FAQ"
              h2Text="You Ask, We Answer"
              h4Size="small"
              h2Size="small"
              align="center"
              h4Color="secondary"
            />
            <FaqAccordion faqs={faqs} />
          </div>
        </div>
      </section>

    </div>
  );
}

export default Subscriptions
